<template>
  <div class="coro-widget">
    <div
      v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED"
      data-testid="emails-widget-no-data-container"
      class="coro-widget__content coro-widget--empty"
    >
      <v-icon class="empty-data-icon" icon="$email" />
      <div class="subtitle1 text-center">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noEmailServicesConnected`) }}
      </div>
      <div class="caption text-indigo-medium mb-4">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.connectSecureCloud`) }}
      </div>
      <v-btn
        v-if="canManageCloudApps"
        :to="{ name: RouteName.CLOUD_APPS_PAGE }"
        color="primary"
        rounded
        data-testid="emails-widget-add-protection-btn"
      >
        {{ $t("general.connect") }}
      </v-btn>
    </div>

    <div v-else-if="widgetDataValue.data" class="coro-widget__content">
      <div class="coro-widget__tile coro-widget__tile-content">
        <div class="mb-3">
          <div class="coro-widget-number text-left" data-testid="emails-widget-scanned-emails">
            {{ widgetDataValue.data.emailsScanned.toLocaleString() }}
          </div>
          <div class="body2">
            {{ $t(`dashboard.widgets.${widgetConfig.type}.emailsScanned`) }} /
            {{ $t(`dashboard.widgets.${widgetConfig.type}.last90Days`) }}
          </div>
        </div>
      </div>
      <div class="coro-widget__tile">
        <vulnerabilities-tile
          :widget-data="widgetDataValue.data"
          @all-active-link-clicked="viewAllTickets(true, ticketsWidgetFilters.EMAILS_WIDGET)"
          @all-caught-up-link-clicked="viewAllTickets(false, ticketsWidgetFilters.EMAILS_WIDGET)"
          @vulnerability-clicked="viewVulnerability($event, ticketsWidgetFilters.EMAILS_WIDGET)"
        ></vulnerabilities-tile>
        <generated-tickets-trend-chart
          v-if="widgetConfig.metadata.state === WidgetState.HAS_OPEN_TICKETS"
          class="pt-2 px-6"
          :chart-data="widgetDataValue.data.generatedTicketsHistory"
          :delta="widgetDataValue.data.generatedTicketsDelta"
        ></generated-tickets-trend-chart>
      </div>
    </div>

    <widget-error-state
      v-else
      :widget-type="widgetConfig.type"
      @retry-click="$emit('fetch-data')"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import kebabCase from "lodash/kebabCase";
import VulnerabilitiesTile from "@/components/VulnerabilitiesTile.vue";
import { TicketsModuleFilter } from "@/constants/tickets";
import { useWidgetContent } from "@/composables/useWidgetContent";
import { RouteName } from "@/constants/routes";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import GeneratedTicketsTrendChart from "@/components/dashboard/GeneratedTicketsTrendChart.vue";
import type { WidgetType } from "@/constants/dashboard.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.EMAIL_SECURITY>>,
      required: true,
    },
  },
  components: {
    WidgetErrorState,
    GeneratedTicketsTrendChart,
    VulnerabilitiesTile,
  },
  setup(props) {
    const { viewVulnerability, viewAllTickets, isAccessRestricted } = useWidgetContent();

    const widgetDataValue = computed(() => props.widgetConfig.data);

    const canManageCloudApps = computed(
      () =>
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.CLOUD_APPS
        )
    );

    return {
      canManageCloudApps,
      widgetDataValue,
      viewVulnerability,
      viewAllTickets,
      isAccessRestricted,
      ticketsWidgetFilters: TicketsModuleFilter,
      kebabCase,
      RouteName,
      WidgetState,
    };
  },
});
</script>
